import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Info, Loader2, Pin, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation, useParams } from "wouter";
import { z } from "zod";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

const surveyFormSchema = z
  .object({
    title: z.string().min(2, "Title is required"),
    description: z.string().optional(),
    surveyType: z.enum(["adhoc", "recurring"]),
    recurrenceInDays: z.number().optional(),
    deadlineInDays: z.number().min(1).default(7),
    firstSendDate: z.string().optional(),
    questions: z
      .array(
        z.object({
          id: z.string().optional(),
          questionText: z.string().min(1, "Question is required"),
          questionDescription: z.string().optional(),
          questionType: z
            .enum(["short_text", "long_text", "single_choice", "scale"])
            .default("short_text"),
          required: z.boolean().default(false),
          scaleMin: z.number().optional(),
          scaleMax: z.number().optional(),
          scaleMinLabel: z.string().optional(),
          scaleMaxLabel: z.string().optional(),
          options: z
            .array(z.object({ label: z.string().min(1, "Option label is required") }))
            .optional(),
        }),
      )
      .min(1, "At least one question is required"),
  })
  .refine(
    (data) => {
      return data.questions.every((q) => {
        if (q.questionType === "single_choice") {
          return (
            q.options && q.options.length >= 2 && q.options.every((o) => o.label.trim().length > 0)
          );
        }
        return true;
      });
    },
    {
      message: "Perguntas de escolha única precisam de pelo menos 2 opções preenchidas",
      path: ["questions"],
    },
  );

type SurveyFormValues = z.infer<typeof surveyFormSchema>;

export default function SurveyFormPage() {
  const params = useParams<{ id: string }>();
  const surveyId = params?.id;
  const isEditMode = !!surveyId;
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: existingSurvey, isLoading: isLoadingSurvey } = useQuery({
    ...orpc.pulseSurveys.getById.queryOptions({ input: { id: surveyId! } }),
    enabled: isEditMode,
  });

  const form = useForm<SurveyFormValues>({
    resolver: zodResolver(surveyFormSchema),
    defaultValues: {
      title: "",
      description: "",
      surveyType: "adhoc",
      deadlineInDays: 7,
      questions: [{ questionText: "", questionType: "short_text" as const, required: false }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const surveyType = form.watch("surveyType");

  useEffect(() => {
    if (isEditMode && existingSurvey) {
      const questions = existingSurvey.questions?.map((q) => ({
        id: q.id,
        questionText: q.questionText,
        questionDescription: q.description || "",
        questionType:
          (q.questionType as "short_text" | "long_text" | "single_choice" | "scale") ||
          "short_text",
        required: q.required ?? false,
        scaleMin: q.scaleMin ?? undefined,
        scaleMax: q.scaleMax ?? undefined,
        scaleMinLabel: q.scaleMinLabel || "",
        scaleMaxLabel: q.scaleMaxLabel || "",
        options: q.options ? (q.options as Array<{ label: string }>) : undefined,
      })) || [{ questionText: "", questionType: "short_text" as const, required: false }];

      form.reset({
        title: existingSurvey.title,
        description: existingSurvey.description || "",
        surveyType: (existingSurvey.surveyType as "adhoc" | "recurring") || "adhoc",
        recurrenceInDays: existingSurvey.recurrenceInDays ?? undefined,
        deadlineInDays: existingSurvey.deadlineInDays ?? 7,
        firstSendDate: existingSurvey.nextInstanceDate
          ? new Date(existingSurvey.nextInstanceDate).toISOString().slice(0, 16)
          : undefined,
        questions,
      });
    }
  }, [isEditMode, existingSurvey, form]);

  const buildPayload = (data: SurveyFormValues) => ({
    ...data,
    questions: data.questions.map((q) => ({
      id: q.id || undefined,
      questionText: q.questionText,
      description: q.questionDescription || null,
      questionType: q.questionType,
      required: q.required,
      scaleMin: q.scaleMin,
      scaleMax: q.scaleMax,
      scaleMinLabel: q.scaleMinLabel,
      scaleMaxLabel: q.scaleMaxLabel,
      options:
        q.questionType === "single_choice" && q.options?.length
          ? q.options.map((o) => ({ label: o.label }))
          : null,
    })),
  });

  const createSurvey = useMutation({
    mutationFn: async (data: SurveyFormValues) => {
      return rpcClient.pulseSurveys.create(buildPayload(data));
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: orpc.pulseSurveys.list.queryOptions().queryKey });
      toast({ title: "Pesquisa criada com sucesso" });
      setLocation(data?.id ? `/surveys/${data.id}` : "/surveys");
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao criar pesquisa",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateSurvey = useMutation({
    mutationFn: async (data: SurveyFormValues) => {
      return rpcClient.pulseSurveys.update({ id: surveyId!, ...buildPayload(data) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.pulseSurveys.list.queryOptions().queryKey });
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.getById.queryOptions({ input: { id: surveyId! } }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.getDetail.queryOptions({ input: { id: surveyId! } }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.listInstances.queryOptions({ input: { id: surveyId! } })
          .queryKey,
      });
      toast({ title: "Pesquisa atualizada com sucesso" });
      setLocation(`/surveys/${surveyId}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar pesquisa",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SurveyFormValues) => {
    if (isEditMode) {
      updateSurvey.mutate(data);
    } else {
      createSurvey.mutate(data);
    }
  };

  const isPending = createSurvey.isPending || updateSurvey.isPending;

  if (isEditMode && isLoadingSurvey) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditMode ? "Editar Pesquisa" : "Nova Pesquisa"}
        description={
          isEditMode
            ? "Atualize os dados da pesquisa de clima"
            : "Crie uma nova pesquisa de clima organizacional"
        }
        onBack={() => setLocation(isEditMode ? `/surveys/${surveyId}` : "/surveys")}
      />

      {isEditMode && (
        <div
          className="flex items-start gap-3 rounded-md border border-border bg-muted/50 p-4"
          data-testid="info-edit-notice"
        >
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            As alterações realizadas aqui serão aplicadas apenas em instâncias com status{" "}
            <strong className="text-foreground">Rascunho</strong>. Instâncias ativas, finalizadas ou
            canceladas não serão afetadas.
          </p>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titulo</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Pesquisa Semanal"
                        {...field}
                        data-testid="input-survey-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descricao (opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Breve descricao do objetivo da pesquisa..."
                        className="min-h-[60px]"
                        {...field}
                        data-testid="input-survey-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="surveyType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Pesquisa</FormLabel>
                    <SearchableSelect
                      onValueChange={field.onChange}
                      value={field.value}
                      options={[
                        {
                          value: "adhoc",
                          label: "Pontual (envio manual)",
                          icon: <Pin className="h-3.5 w-3.5" />,
                        },
                        {
                          value: "recurring",
                          label: "Recorrente (aprovação manual)",
                          icon: <RefreshCw className="h-3.5 w-3.5" />,
                        },
                      ]}
                      placeholder="Selecione o tipo"
                      data-testid="select-survey-type"
                    />
                    <FormDescription>
                      {surveyType === "adhoc" &&
                        "Sera enviada manualmente quando voce clicar em 'Enviar'"}
                      {surveyType === "recurring" &&
                        "Um draft é criado automaticamente antes da data agendada; o envio só ocorre após aprovação humana"}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {surveyType === "recurring" && (
                <>
                  <FormField
                    control={form.control}
                    name="firstSendDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data do primeiro envio</FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            data-testid="input-first-send-date"
                          />
                        </FormControl>
                        <FormDescription>
                          Quando a primeira instancia deve ser enviada
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="recurrenceInDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Recorrencia (dias)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="7"
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseInt(e.target.value, 10) : undefined,
                              )
                            }
                            data-testid="input-recurrence-days"
                          />
                        </FormControl>
                        <FormDescription>
                          A cada quantos dias a pesquisa deve ser enviada
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="deadlineInDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prazo para resposta (dias)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="7"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseInt(e.target.value, 10) : undefined)
                        }
                        data-testid="input-deadline-days"
                      />
                    </FormControl>
                    <FormDescription>
                      Quantos dias os colaboradores terao para responder
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <FormLabel>Perguntas</FormLabel>
                {fields.map((field, index) => {
                  const questionType = form.watch(`questions.${index}.questionType`);
                  const options = form.watch(`questions.${index}.options`) || [];
                  return (
                    <Card key={field.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 space-y-2">
                            <FormField
                              control={form.control}
                              name={`questions.${index}.questionText`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      placeholder={`Pergunta ${index + 1}`}
                                      {...field}
                                      data-testid={`input-question-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`questions.${index}.questionDescription`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Descricao da pergunta (opcional)"
                                      className="min-h-[40px] text-sm"
                                      {...field}
                                      data-testid={`input-question-description-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground"
                              onClick={() => remove(index)}
                              data-testid={`button-remove-question-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <FormField
                            control={form.control}
                            name={`questions.${index}.questionType`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">Tipo</FormLabel>
                                <SearchableSelect
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    if (value === "single_choice") {
                                      const currentOptions = form.getValues(
                                        `questions.${index}.options`,
                                      );
                                      if (!currentOptions || currentOptions.length === 0) {
                                        form.setValue(`questions.${index}.options`, [
                                          { label: "" },
                                          { label: "" },
                                        ]);
                                      }
                                    }
                                  }}
                                  value={field.value}
                                  options={[
                                    { value: "short_text", label: "Texto Curto" },
                                    { value: "long_text", label: "Texto Longo" },
                                    { value: "single_choice", label: "Escolha Única" },
                                    { value: "scale", label: "Escala" },
                                  ]}
                                  data-testid={`select-question-type-${index}`}
                                />
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`questions.${index}.required`}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-center space-x-2 space-y-0 pt-6">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    data-testid={`checkbox-required-${index}`}
                                  />
                                </FormControl>
                                <FormLabel className="text-xs font-normal">Obrigatoria</FormLabel>
                              </FormItem>
                            )}
                          />
                        </div>

                        {questionType === "single_choice" && (
                          <div className="space-y-2 pt-2 border-t">
                            <FormLabel className="text-xs">Opcoes</FormLabel>
                            {options.map((_: any, optIndex: number) => (
                              // biome-ignore lint/suspicious/noArrayIndexKey: react-hook-form field array items lack stable ids
                              <div key={optIndex} className="flex items-center gap-2">
                                <FormField
                                  control={form.control}
                                  name={`questions.${index}.options.${optIndex}.label`}
                                  render={({ field }) => (
                                    <FormItem className="flex-1">
                                      <FormControl>
                                        <Input
                                          placeholder={`Opcao ${optIndex + 1}`}
                                          {...field}
                                          data-testid={`input-option-${index}-${optIndex}`}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                {options.length > 1 && (
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="text-muted-foreground"
                                    onClick={() => {
                                      const currentOptions =
                                        form.getValues(`questions.${index}.options`) || [];
                                      form.setValue(
                                        `questions.${index}.options`,
                                        currentOptions.filter(
                                          (_: any, i: number) => i !== optIndex,
                                        ),
                                      );
                                    }}
                                    data-testid={`button-remove-option-${index}-${optIndex}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const currentOptions =
                                  form.getValues(`questions.${index}.options`) || [];
                                form.setValue(`questions.${index}.options`, [
                                  ...currentOptions,
                                  { label: "" },
                                ]);
                              }}
                              data-testid={`button-add-option-${index}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Adicionar Opcao
                            </Button>
                          </div>
                        )}

                        {questionType === "scale" && (
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                            <FormField
                              control={form.control}
                              name={`questions.${index}.scaleMin`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Minimo</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="1"
                                      {...field}
                                      value={field.value ?? ""}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value ? parseInt(e.target.value, 10) : undefined,
                                        )
                                      }
                                      data-testid={`input-scale-min-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`questions.${index}.scaleMax`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Maximo</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      placeholder="5"
                                      {...field}
                                      value={field.value ?? ""}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value ? parseInt(e.target.value, 10) : undefined,
                                        )
                                      }
                                      data-testid={`input-scale-max-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`questions.${index}.scaleMinLabel`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Label Minimo</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Ruim"
                                      {...field}
                                      data-testid={`input-scale-min-label-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`questions.${index}.scaleMaxLabel`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs">Label Maximo</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Excelente"
                                      {...field}
                                      data-testid={`input-scale-max-label-${index}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    append({ questionText: "", questionType: "short_text", required: false })
                  }
                  data-testid="button-add-question"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Adicionar Pergunta
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation(isEditMode ? `/surveys/${surveyId}` : "/surveys")}
                  data-testid="button-cancel"
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isPending} data-testid="button-submit-survey">
                  {isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isEditMode ? "Salvando..." : "Criando..."}
                    </>
                  ) : isEditMode ? (
                    "Salvar Alteracoes"
                  ) : (
                    "Criar Pesquisa"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
